import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Calendar, Mail, Users, FileText, Target, Shield, AlertTriangle } from "lucide-react";
import { BOOKING_URL, CONTACT_EMAIL } from "@/config/constants";

const FORMAT_ITEMS = [
  { text: "Facilitated tabletop exercises simulating real-world AI incidents", icon: AlertTriangle },
  { text: "Scenario-based workshops tailored to your sector and risk profile", icon: Shield },
  { text: "Half-day or full-day sessions (virtual or in-person)", icon: Users },
  { text: "Designed for board members, executives, and governance teams together", icon: Target },
];

const DELIVERABLES = [
  "Custom scenario pack aligned to your organization's AI footprint",
  "Board incident escalation playbook",
  "Materiality decision tree for AI-related incidents",
  "After-action report with prioritized improvement recommendations",
  "Updated board reporting pack incorporating lessons learned",
];

const OUTCOMES = [
  "Test your committee's readiness to respond to AI-related incidents",
  "Identify gaps in escalation, communication, and decision-making",
  "Build muscle memory for high-stakes AI governance decisions",
  "Align board and management on roles, thresholds, and response protocols",
  "Strengthen your governance posture with documented, defensible processes",
];

const ScenarioActivationsPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="Scenario Activations | BoardSight"
        description="Facilitated tabletop exercises and scenario-based workshops to test and strengthen your Audit & Risk Committee's AI governance readiness."
        canonicalUrl="https://boardsight.ai/scenario-activations"
      />

      {/* Hero */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Scenario Activations
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Stress-test your Audit &amp; Risk Committee's AI governance readiness with facilitated tabletop exercises and scenario-based workshops—built for the boardroom, not the lab.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Format - Cards with icons */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Format</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              {FORMAT_ITEMS.map((item, i) => (
                <Card key={i} className="bg-card border-border">
                  <CardContent className="flex items-start gap-4 pt-6">
                    <item.icon className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item.text}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables - Numbered list */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Deliverables</h2>
            <div className="space-y-4">
              {DELIVERABLES.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </span>
                  <span className="text-lg text-foreground pt-0.5">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes - Checklist */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Outcomes</h2>
            <ul className="space-y-4">
              {OUTCOMES.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Ready to activate your committee's AI governance response?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get in touch to design a scenario activation tailored to your Audit or Risk Committee.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Book a Chair Call
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href={`mailto:${CONTACT_EMAIL}`} className="inline-flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {CONTACT_EMAIL}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ScenarioActivationsPage;
